import { useMemo } from "react";
import { 
  Send, CheckCircle2, XCircle, Package, Download, AlertTriangle 
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { toast } from "sonner";

export const SubmissionCard = ({
  missionId,
  previousGenerations = [],
  reviewResult,
  language = "cs"
}) => {
  // Translations
  const t = language === 'cs' ? {
    title: "Podání ÚCL",
    documentChecklist: "Dokumenty pro podání",
    conops: "ConOps",
    om: "Operations Manual",
    uclPackage: "UCL balíček",
    review: "Review dokončen",
    generated: "vygenerován",
    missing: "chybí",
    completed: "dokončen",
    notDone: "nedokončen",
    readyToSubmit: "Připraveno k podání",
    missingDocuments: "Chybí dokumenty",
    downloadPackage: "Stáhnout kompletní balík",
    packageContents: "Obsahuje všechny dokumenty pro ÚCL",
    helpText: "Potřebujete pomoc s podáním?",
    helpEmail: "info@aeroway.cz"
  } : {
    title: "CAA Submission",
    documentChecklist: "Documents for Submission",
    conops: "ConOps",
    om: "Operations Manual",
    uclPackage: "UCL Package",
    review: "Review completed",
    generated: "generated",
    missing: "missing",
    completed: "completed",
    notDone: "not done",
    readyToSubmit: "Ready to Submit",
    missingDocuments: "Missing Documents",
    downloadPackage: "Download Complete Package",
    packageContents: "Includes all documents for CAA",
    helpText: "Need help with submission?",
    helpEmail: "info@aeroway.cz"
  };
  
  // Check document readiness
  const readiness = useMemo(() => {
    const completedGens = previousGenerations.filter(g => g.status === "completed" && g.has_content);
    
    const hasConops = completedGens.some(g => g.generation_type === "conops");
    const hasOm = completedGens.some(g => g.generation_type === "om");
    const hasUcl = completedGens.some(g => g.generation_type === "ucl_package");
    const hasReview = !!reviewResult && reviewResult.status !== "failed";
    
    const allReady = hasConops && hasOm && hasReview;
    const readyCount = [hasConops, hasOm, hasUcl, hasReview].filter(Boolean).length;
    
    return { hasConops, hasOm, hasUcl, hasReview, allReady, readyCount };
  }, [previousGenerations, reviewResult]);
  
  // Handle download package
  const handleDownloadPackage = () => {
    // In production, this would trigger a zip download of all documents
    toast.success(language === 'cs' ? 'Stahování balíku...' : 'Downloading package...');
  };
  
  // Checklist item component
  const ChecklistItem = ({ label, ready, subLabel }) => (
    <div className="flex items-center justify-between p-2 rounded-lg bg-[var(--bg-elevated)]">
      <div className="flex items-center gap-2">
        {ready ? (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        ) : (
          <XCircle className="h-4 w-4 text-red-400" />
        )}
        <span className={`text-sm ${ready ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}`}>
          {label}
        </span>
      </div>
      <span className={`text-xs ${ready ? 'text-green-600' : 'text-red-500'}`}>
        {ready ? subLabel || t.generated : t.missing}
      </span>
    </div>
  );

  return (
    <div className="p-4 rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] shadow-sm" data-testid="submission-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Send className="h-5 w-5 text-[#1E3A5F]" />
          <span className="font-medium text-[var(--text-primary)]">{t.title}</span>
        </div>
        {readiness.allReady ? (
          <Badge className="bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 text-xs">
            {t.readyToSubmit}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
            {t.missingDocuments}
          </Badge>
        )}
      </div>
      
      {/* Document Checklist */}
      <div className="space-y-2 mb-4">
        <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
          {t.documentChecklist}
        </p>
        <ChecklistItem label={t.conops} ready={readiness.hasConops} />
        <ChecklistItem label={t.om} ready={readiness.hasOm} />
        <ChecklistItem label={t.uclPackage} ready={readiness.hasUcl} />
        <ChecklistItem label={t.review} ready={readiness.hasReview} subLabel={t.completed} />
      </div>
      
      {/* Download Package Button */}
      {readiness.allReady ? (
        <div className="space-y-2">
          <Button 
            onClick={handleDownloadPackage}
            className="w-full gap-2 bg-[#1D9E75] hover:bg-[#178a66]"
          >
            <Package className="h-4 w-4" />
            {t.downloadPackage}
          </Button>
          <p className="text-xs text-center text-[var(--text-muted)]">
            {t.packageContents}
          </p>
        </div>
      ) : (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-center">
          <AlertTriangle className="h-6 w-6 mx-auto text-amber-500 mb-2" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {language === 'cs' 
              ? `Zbývá dokončit ${4 - readiness.readyCount} z 4 položek`
              : `${4 - readiness.readyCount} of 4 items remaining`}
          </p>
        </div>
      )}
      
      {/* Help Section */}
      <div className="mt-4 pt-4 border-t border-[var(--border-default)] text-center">
        <p className="text-xs text-[var(--text-muted)]">{t.helpText}</p>
        <a href={`mailto:${t.helpEmail}`} className="text-xs text-blue-600 hover:underline font-medium">
          {t.helpEmail}
        </a>
      </div>
    </div>
  );
};

export default SubmissionCard;
